import React, { useEffect, useId, useRef, useState } from 'react';

import { useEsCopyRecheck } from './EsCopy';
import { useEsTranslationsFlag } from './EsFlags';
import { EsLocaleName, esLocaleMark } from './EsLocaleFlag';
import { useEsStrings } from './EsStrings';
import { LOCALE_FALLBACK, useEsCurrentLocale, useEsLocalePick, useEsLocales } from './EsLocale';

/**
 * The nav's language menu, beside the theme disc: a disc the theme's size showing the reader's
 * language's mark alone -- its flag, else its badge, else the globe -- and a panel of the locales
 * escapade is translated into, each row its mark and its own name (EsLocaleName). The name is
 * not drawn on the button (Tim, 2026-10-19); it is still the button's accessible name.
 *
 * Only LIVE groups are listed -- esback's `live`, own coverage at or past 95 % -- plus en-US and
 * the reader's own group: a group a release has just left a few strings behind in stays, and
 * an empty es-419 never sits beside es saying the same words. An es-MX reader sees es marked.
 *
 * The same positioned, mounted-and-faded panel as EsThemeMenu, for its reasons. Nothing at all
 * while rolloutEsTranslations is off, so the bar is the bar it was.
 */
export function esMenuLocales( pLocales, pCurrent ) {
    return ( pLocales ?? [] )
        .filter( ( pGroup ) => pGroup.live || pGroup.key === LOCALE_FALLBACK || pGroup.key === pCurrent )
        .sort( ( pA, pB ) => ( pA.tier ?? 9 ) - ( pB.tier ?? 9 )
            || String( pA.nativeName ?? pA.key ).localeCompare( String( pB.nativeName ?? pB.key ) ) );
}

export default function EsLocaleMenu() {
    const aOn = useEsTranslationsFlag();
    useEsCopyRecheck();
    return aOn ? <EsLocalePanel /> : null;
}

function EsLocalePanel() {
    const t = useEsStrings();
    const aCurrent = useEsCurrentLocale();
    const aLocales = useEsLocales();
    const aRows = esMenuLocales( aLocales, aCurrent );
    const aGroup = ( aLocales ?? [] ).find( ( pGroup ) => pGroup.key === aCurrent );
    const aMark = aGroup ? esLocaleMark( aGroup.key ) : null;
    const pick = useEsLocalePick();
    const [ open, setOpen ] = useState( false );
    const box = useRef( null );
    const aId = useId();

    // Close on a click anywhere else, and on Escape, as the theme menu does.
    useEffect( () => {
        if ( !open ) return undefined;

        const aClick = ( pEvent ) => {
            if ( box.current && !box.current.contains( pEvent.target ) ) setOpen( false );
        };
        const aKey = ( pEvent ) => {
            if ( pEvent.key === 'Escape' ) setOpen( false );
        };

        document.addEventListener( 'mousedown', aClick );
        document.addEventListener( 'keydown', aKey );
        return () => {
            document.removeEventListener( 'mousedown', aClick );
            document.removeEventListener( 'keydown', aKey );
        };
    }, [ open ] );

    return (
        <div ref={ box } className="relative">
            <button
                type="button"
                data-testid="es-locale-menu-trigger"
                aria-labelledby={ aGroup ? aId + '-label ' + aId + '-name' : aId + '-label' }
                aria-expanded={ open }
                aria-haspopup="menu"
                onClick={ () => setOpen( ( pWas ) => !pWas ) }
                data-tip={ t( 'common-language', 'Language' ) }
                className="esNavDisc ms-1 flex sm:ms-2 cursor-pointer items-center justify-center rounded-full bg-nav-disc text-nav-glyph hover:bg-nav-disc-hover">
                { /* Named "Language" and then the language shown, so a reader of the mark hears the
                     words it stands for. aria-labelledby joins the two with a space and reads
                     hidden elements, so neither is drawn. */ }
                <span id={ aId + '-label' } hidden>{ t( 'common-language', 'Language' ) }</span>
                { aGroup && <span id={ aId + '-name' } lang={ aGroup.key } hidden>{ aGroup.nativeName ?? aGroup.key }</span> }
                { aMark?.flag && <span aria-hidden="true" data-mark="flag" className="text-lg leading-none">{ aMark.flag }</span> }
                { aMark?.badge && <span aria-hidden="true" data-mark="badge" className="rounded-sm border border-current px-0.5 text-[0.6rem] font-semibold leading-tight opacity-80">{ aMark.badge }</span> }
                { !aMark && <i className="pi pi-globe text-lg leading-none" aria-hidden="true" /> }
            </button>

            <ul
                data-testid="es-locale-menu"
                role="menu"
                aria-label={ t( 'common-language', 'Language' ) }
                aria-hidden={ !open }
                className={ 'absolute inset-e-0 top-[calc(100%+0.5rem)] z-[99999] flex max-h-[70vh] w-48 flex-col overflow-y-auto'
                    + ' rounded-lg border border-nav-menu-edge bg-panel p-1 text-sm text-title shadow-lg transition-all ease-in-out'
                    + ( open ? ' opacity-100' : ' pointer-events-none opacity-0' ) }>
                { aRows.map( ( pGroup ) => (
                    <li key={ pGroup.key } role="none">
                        <button
                            type="button"
                            role="menuitemradio"
                            data-testid={ 'es-locale-menu-' + pGroup.key }
                            lang={ pGroup.key }
                            aria-checked={ pGroup.key === aCurrent }
                            tabIndex={ open ? undefined : -1 }
                            onClick={ () => { pick( pGroup.key ); setOpen( false ); } }
                            className={ 'flex w-full cursor-pointer items-center justify-between gap-2 rounded-md px-2 py-1 text-start'
                                + ' hover:bg-nav-disc-hover focus:outline-none focus-visible:ring-2 ring-focus'
                                + ( pGroup.key === aCurrent ? ' font-bold' : '' ) }>
                            <EsLocaleName group={ pGroup } />
                            { pGroup.key === aCurrent && <i className="pi pi-check text-xs" aria-hidden="true" /> }
                        </button>
                    </li>
                ) ) }
            </ul>
        </div>
    );
}
